// Forms embed bundle entry — autoInit dispatcher.
//
// Scans the DOM for [id^="aldelo-form-"] containers, parses each
// container's data-config JSON into a type-safe FormsEmbedConfig,
// and mounts a FormsEmbed per container in an isolated React root
// wrapped in ErrorBoundary. One container crash does NOT affect
// siblings (Doc 05 section 13.2 isolation pattern).
//
// Inline styles only — no Tailwind. NO imports from portal/.

import React from "react";
import { createRoot } from "react-dom/client";
import type { FormsEmbedConfig } from "./FormsEmbed";
import { FormsEmbed } from "./FormsEmbed";
import { SLUG_RE } from "./slug";

const MOUNT_PREFIX = "aldelo-form-";

// -- ErrorBoundary (inline — keeps bundle entry self-contained) -------------

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[aldelo-embed] Uncaught error in forms embed:", error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            padding: 16,
            borderRadius: 8,
            backgroundColor: "#FEF2F2",
            border: "1px solid #DC2626",
            color: "#DC2626",
            fontSize: 14,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          Something went wrong loading this form. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

// -- Config parser -----------------------------------------------------------

type ThemeValue = "light" | "dark";

function isValidTheme(v: unknown): v is ThemeValue {
  return v === "light" || v === "dark";
}

export function parseConfig(json: string): FormsEmbedConfig {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Invalid JSON in data-config");
  }
  if (typeof parsed !== "object" || parsed === null) {
    throw new Error("data-config must be a JSON object");
  }
  const obj = parsed as Record<string, unknown>;

  if (typeof obj.apiBase !== "string" || !obj.apiBase) {
    throw new Error("data-config missing required field: apiBase");
  }
  if (typeof obj.formSlug !== "string" || !SLUG_RE.test(obj.formSlug)) {
    throw new Error("data-config has invalid or missing formSlug");
  }

  const config: FormsEmbedConfig = {
    apiBase: obj.apiBase,
    formSlug: obj.formSlug,
  };

  if (typeof obj.title === "string") config.title = obj.title;
  if (isValidTheme(obj.theme)) config.theme = obj.theme;
  if (typeof obj.recaptchaSiteKey === "string") config.recaptchaSiteKey = obj.recaptchaSiteKey;

  return config;
}

// -- autoInit ----------------------------------------------------------------

// Containers we have already attempted to mount. Guards against double-mount:
// React's createRoot() throws if called twice on the same node, and the
// MutationObserver below re-scans on every DOM mutation (including the subtree
// churn createRoot itself produces). A WeakSet keeps this GC-friendly and
// avoids writing a visible marker attribute into the operator's DOM.
const handled = new WeakSet<Element>();

// mountContainer parses one container's data-config and mounts a FormsEmbed
// into it. Idempotent: a container is processed at most once (success OR
// invalid-config), so repeated scans / observer callbacks never double-mount
// or re-log.
function mountContainer(container: HTMLElement): void {
  if (handled.has(container)) return;
  handled.add(container);

  const configJson = container.getAttribute("data-config");
  if (!configJson) {
    console.error("[aldelo-embed] Missing data-config on", container.id);
    return;
  }

  let config: FormsEmbedConfig;
  try {
    config = parseConfig(configJson);
  } catch (err) {
    console.error("[aldelo-embed] Invalid data-config on", container.id, err);
    return;
  }

  const root = createRoot(container);
  root.render(
    <ErrorBoundary>
      <FormsEmbed config={config} />
    </ErrorBoundary>,
  );
}

export function autoInit(): void {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll<HTMLElement>(`[id^="${MOUNT_PREFIX}"]`)
    .forEach((container) => mountContainer(container));
}

// -- observeMounts -----------------------------------------------------------
//
// Bug A fix: a one-shot DOMContentLoaded scan loses a race on Framer (and other
// SPA) hosts, where the mount <div> is rendered client-side AFTER the bundle
// runs autoInit. Safari's script scheduling makes that loss reliable on a cold
// load, leaving the form stuck on the host's "Loading form..." placeholder
// until a manual refresh. A MutationObserver watches for mount points that
// appear after the initial scan — covering late client render AND SPA
// client-side navigation (where DOMContentLoaded never re-fires). Mounting is
// idempotent via `handled`, so the initial scan and the observer can both see
// the same node without double-mounting.

let mountObserver: MutationObserver | null = null;

export function observeMounts(): void {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  if (mountObserver) return; // singleton — one observer per document

  mountObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        // The added node may itself be a mount point, or contain one nested
        // inside wrapper elements (Framer wraps components in layout divs).
        if (node.id && node.id.startsWith(MOUNT_PREFIX)) {
          mountContainer(node);
        }
        node
          .querySelectorAll<HTMLElement>(`[id^="${MOUNT_PREFIX}"]`)
          .forEach((el) => mountContainer(el));
      });
    }
  });

  mountObserver.observe(document.documentElement, { childList: true, subtree: true });
}

export function stopObservingMounts(): void {
  if (mountObserver) {
    mountObserver.disconnect();
    mountObserver = null;
  }
}

// Auto-run: in a browser, scan immediately when possible and arm the observer
// so mounts that appear later (Framer client render, SPA navigation) are
// caught. autoInit + observeMounts share the idempotent `handled` guard.
if (typeof window !== "undefined") {
  const start = (): void => {
    autoInit();
    observeMounts();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
